import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useCompletedInventoryRequestList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refCompletedInventoryRequestsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'createdBy',
      label: 'Requester',
      sortable: true,
    },
    {
      key: 'serviceDate',
      label: 'Service request Date/time',
      sortable: true,
    },
    {
      key: 'customerName',
      label: 'Customer name/File no.',
      sortable: true,
    },
    {
      key: 'stores',
      label: 'Store(s)',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalInventoryRequests = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc ? (route.query.sortDesc === 'true') : true)
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const dutyOfficerFilter = ref([])
  if (Array.isArray(route.query.requester)) {
    dutyOfficerFilter.value = route.query.requester
  } else if (route.query.requester) {
    dutyOfficerFilter.value = [route.query.requester]
  }
  const sfFilter = ref([])
  if (Array.isArray(route.query.sForm)) {
    sfFilter.value = route.query.sForm
  } else if (route.query.sForm) {
    sfFilter.value = [route.query.sForm]
  }
  const storeFilter = ref([])
  if (Array.isArray(route.query.store)) {
    storeFilter.value = route.query.store
  } else if (route.query.store) {
    storeFilter.value = [route.query.store]
  }
  const toDeletedID = ref(null)
  const type = ref('completed')
  const inventoryRequestData = ref([])
  const serviceDateFilter = ref(route.query.serviceDate || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refCompletedInventoryRequestsTable.value ? refCompletedInventoryRequestsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInventoryRequests.value,
    }
  })

  const refetchData = () => {
    refCompletedInventoryRequestsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, sfFilter, serviceDateFilter, dutyOfficerFilter, storeFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchInventoryRequests = (ctx, callback) => {
    store
      .dispatch('app-completed-inventory-requests/fetchInventoryRequests', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        sForm: sfFilter.value,
        serviceDate: serviceDateFilter.value,
        requester: dutyOfficerFilter.value,
        store: storeFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { inventoryRequests, total } = response.data
        inventoryRequestData.value = inventoryRequests
        callback(inventoryRequests)
        totalInventoryRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              sForm: sfFilter.value,
              serviceDate: serviceDateFilter.value,
              requester: dutyOfficerFilter.value,
              store: storeFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteInventoryRequest = () => {
    store
      .dispatch('app-completed-inventory-requests/deleteInventoryRequest', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveInventoryRequestStatusVariant = status => {
    if (status === 'completed') return 'success'
    if (status === 'cancelled') return 'danger'
    if (status === 'returned') return 'warning'
    if (status === 'collected') return 'secondary'
    if (status === 'open') return 'info'
    return 'primary'
  }

  return {
    fetchInventoryRequests,
    tableColumns,
    perPage,
    currentPage,
    totalInventoryRequests,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCompletedInventoryRequestsTable,

    resolveInventoryRequestStatusVariant,
    refetchData,

    toDeletedID,
    deleteInventoryRequest,
    inventoryRequestData,

    // Extra Filters
    statusFilter,
    sfFilter,
    serviceDateFilter,
    dutyOfficerFilter,
    storeFilter,
  }
}
